import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import FaArrowLeft from 'react-icons/lib/ti/arrow-left';
import { connect } from 'react-redux';
import { browserHistory } from 'react-router';

import { compose } from 'ramda';
import {
  clearClipperAction,
  createVideoFromUrl,
  extractFromUrl,
  createVideoFromFile,
  loadFile,
  log,
  editUserAction,
} from 'src/actions';
import AnimatedLogoWhite from 'src/assets/logo-white-transparent.gif';
import AnimatedLogo from 'src/assets/logo.gif';
import { withProviders } from 'src/common/hocs/with-providers';
import { selectActiveLabelId } from 'src/dashboard/state/labels/labels-selectors';
import { getQueryParamsForActiveLabel } from 'src/dashboard/state/labels/labels-utils';
import { setCookie } from 'src/misc/cookies';
import ExceedsLimitsModal from 'src/misc/modals/ExceedsLimitsModal';
import { selectIsMobile, selectIsIOs } from 'src/misc/selectors';
import VideoEditor from 'src/misc/videoEditor/VideoEditor';

import ClipperHeader from './ClipperHeader';
import './clipper.css';
import DailyMotionPlayer from './players/DailyMotion';
import YouTubePlayer from './players/YouTube';

const DEFAULT_FREE_PLAN_MAX_LENGTH = 600;

const mapStateToProps = (state, ownProps) => ({
  ...state.clipper,
  extract: state.clipper.extract ? state.clipper.extract.toObject() : null,
  query: ownProps.query,
  mobile: selectIsMobile(state),
  isIOs: selectIsIOs(state),
  me: state.me,
  activeLabelId: selectActiveLabelId(state),
});

const mapDispatchToProps = (dispatch) => ({
  dispatch,
  createVideoFromUrl: (
    url,
    options,
    source,
    extractor,
    extract_id,
    activeLabelId
  ) => {
    setCookie('dashboard', 'true', 90000);
    const queryParams = getQueryParamsForActiveLabel(activeLabelId);
    browserHistory.push(`/?${queryParams}`);
    return dispatch(
      createVideoFromUrl(url, options, {
        source,
        extractor,
        extract_id,
      })
    ).then(() => {
      dispatch(clearClipperAction());
    });
  },
  createVideoFromFile: (file, options, activeLabelId) => {
    const queryParams = getQueryParamsForActiveLabel(activeLabelId);
    browserHistory.push(`/?${queryParams}`);
    return dispatch(
      createVideoFromFile(file, {
        ...options,
      })
    ).then(() => {
      dispatch(clearClipperAction());
    });
  },
});

class Clipper extends Component {
  constructor() {
    super();
    this.state = {
      saving: false,
      tooLong: false,
    };
    this.setState = this.setState.bind(this);
    this.onFinish = this.onFinish.bind(this);
  }

  componentWillMount() {
    const { dispatch, query } = this.props;
    // we're loading from query string'
    if (typeof window !== 'undefined' && query.url) {
      dispatch(extractFromUrl(this.props.query.url));
    }
    if (typeof window !== 'undefined' && window.STM_UPLOAD_FILE) {
      dispatch(loadFile(window.STM_UPLOAD_FILE));
      window.STM_UPLOAD_FILE = null;
    }
    this.mounted = true;
  }

  componentWillUnmount() {
    this.mounted = false;
    this.props.dispatch(clearClipperAction());
  }

  componentDidUpdate(prevProps) {
    // If the extractor is TikTok, it should be automatically added to the library, bypassing the editor screen, since the preview of TikTok does not work
    const extractor = this.props.extract?.extractor;
    if (extractor === 'TikTok' && extractor !== prevProps.extract.extractor) {
      const options = { mute: false, thumb_offset: null };
      this.onFinish(options);
    }
  }

  onFinish(options) {
    if (
      this.props.me.plan_max_length &&
      options.length > this.props.me.plan_max_length
    ) {
      this.props.dispatch(
        editUserAction({
          videoExceedsLimits: 'clipper-toolong',
        })
      );
      return;
    } else if (
      this.props.me.plan &&
      this.props.me.subscription_status === 'UNPAID'
    ) {
      if (options.length > DEFAULT_FREE_PLAN_MAX_LENGTH) {
        this.props.dispatch(
          editUserAction({
            videoExceedsLimits: 'clipper-toolong',
          })
        );
        return;
      }
    }
    options.title = this.props.extract.title;
    options.extractor = this.props.extract.extractor;
    options.upload_source = this.props.query.upload_source || 'clip';
    if (this.props.source_url) {
      options.source = this.props.source_url;
    }
    if (this.props.extract.proxy) {
      options.proxy = this.props.extract.proxy;
    }
    if (this.props.extract.headers) {
      options.headers = this.props.extract.headers;
    }
    if (this.props.extract.audio_url) {
      options.audio_url = this.props.extract.audio_url;
    }
    if (this.props.extract.captions) {
      options.captions = this.props.extract.captions;
    }

    this.setState({ saving: true });
    const afterFunc = () => {
      if (!this.mounted) {
        return;
      }
      this.setState({ saving: false });
    };
    setCookie('dashboard', 'true', 90000);
    if (this.props.extract.file) {
      this.props
        .createVideoFromFile(
          this.props.extract.file,
          options,
          this.props.activeLabelId
        )
        .then(afterFunc);
    } else {
      this.props
        .createVideoFromUrl(
          this.props.extract.video_url || this.props.extract.url,
          options,
          this.props.extract.source_url,
          this.props.extract.extractor,
          this.props.extract.id,
          this.props.activeLabelId
        )
        .then(afterFunc);
    }
  }

  render() {
    return (
      <div className="clipper-container">
        <ExceedsLimitsModal />
        <a
          className="back"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            this.props.dispatch(clearClipperAction());
            browserHistory.goBack();
          }}
        >
          <FaArrowLeft />
        </a>
        <div className="clipper">
          <Helmet>
            <title>{window._STM_PREDATA.clipper.title}</title>
            <meta
              name="description"
              content={window._STM_PREDATA.clipper.description}
            ></meta>
            <link rel="canonical" href={`https://streamable.com/clipper`} />
          </Helmet>
          {this.props.extract ? (
            (() => {
              if (this.props.extract.error) {
                const isYoutubeError = this.props.extract.error.includes(
                  'The clipper no longer supports YouTube links'
                );

                if (isYoutubeError) {
                  return (
                    <div className="clipper-error">
                      <ClipperHeader url={this.props.query.url} />
                      <i>
                        The clipper no longer supports YouTube links. We're
                        happy to offer refunds to any paid customers as a result
                        of this change, please,{' '}
                        <a href="https://support.streamable.com/contact-us">
                          reach out to Support
                        </a>
                        .
                      </i>
                    </div>
                  );
                }

                return (
                  <div className="clipper-error">
                    <ClipperHeader url={this.props.query.url} />
                    <i>
                      {this.props.extract.message || this.props.extract.error}
                    </i>
                    <ul style={{ marginTop: '25px', maxWidth: '300px' }}>
                      <li>
                        Try clicking the <b>Share Button</b> on the video and
                        use that link instead.
                      </li>
                      <li>
                        <a href="https://support.streamable.com/contact-us">
                          Contact support
                        </a>{' '}
                        and report the issue.
                      </li>
                    </ul>
                  </div>
                );
              }
              if (this.props.extract.isFetching || this.state.saving) {
                return (
                  <div className="loading-indicator">
                    <img
                      alt="Animated Logo"
                      role="presentation"
                      src={
                        this.props.me.dark_mode
                          ? AnimatedLogoWhite
                          : AnimatedLogo
                      }
                    />
                  </div>
                );
              }
              const playerParams = {};
              if (this.props.extract.player === 'dailymotion') {
                playerParams.Player = DailyMotionPlayer;
                playerParams.videoId = this.props.extract.id;
              }
              if (this.props.extract.player === 'youtube') {
                playerParams.Player = YouTubePlayer;
                playerParams.videoId = this.props.extract.id;
              }

              return (
                <VideoEditor
                  action={this.props.query.action}
                  mobile={this.props.mobile}
                  url={this.props.extract.playback_url}
                  mime={this.props.extract.mime}
                  maxLength={this.props.me.plan_max_length}
                  autoPlay={true}
                  defaultTitle={this.props.query.title}
                  doneButtonText="Create Video"
                  videoHeight={this.props.extract.height}
                  videoWidth={this.props.extract.width}
                  source={this.props.source_url}
                  durationGuess={
                    this.props.extract.player === 'youtube'
                      ? this.props.extract.duration
                      : null
                  }
                  onPlaybackError={(data) => {
                    log(`[FE Error] Playback failure: ${JSON.stringify(data)}`);
                  }}
                  onDuration={(duration) => {
                    this.setState({
                      tooLong: this.props.me.plan_max_length
                        ? duration > this.props.me.plan_max_length
                        : false,
                      duration,
                    });
                  }}
                  onFinish={this.onFinish}
                  {...playerParams}
                />
              );
            })()
          ) : (
            <ClipperHeader url={this.props.query.url} />
          )}
        </div>
      </div>
    );
  }
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withProviders({ design: 'v1' })
)(Clipper);
